import React, { useState, useEffect } from 'react';
import { UiKit } from '@datavant/uikit';
import Image from 'next/image';
import '@okta/okta-signin-widget/css/okta-sign-in.min.css';
import styles from '../EmbeddedLogin.module.css';
import { EmailDiscovery } from './EmailDiscovery';
import { useOktaAuth } from '@okta/okta-react';
import { tRPCProxyClient } from '../../../utils/trpc';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { Register } from './Register';
import { useMediaQuery, useToast } from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { HelpFooter } from '../../HelpFooter';
import { ColoredBox } from '../../../../shared/components/ColoredBox';

const ICON = {
    color: '#006CCF',
    size: 'sm',
} as const;

export const Page = (props: { autoForwarded: boolean }) => {
    const [flow, setFlow] = useState<null | 'login' | 'register'>(null);
    const [email, setEmail] = useState<string>('');
    const { oktaAuth, authState } = useOktaAuth();
    const router = useRouter();
    const toast = useToast();
    const { fromLogin, redirect } = router.query;
    const autoForwarded = true; //props.autoForwarded;
    const [isSmallScreen] = useMediaQuery('(max-width: 1366px) and (max-height: 768px)');

    const EmbeddedLogin = dynamic(() => import('./EmbeddedLogin'), {
        ssr: false,
    });

    const handleAlreadyAuthenticated = async () => {
        if (authState?.isAuthenticated && authState.accessToken) {
            await tRPCProxyClient.session.createUserSession.mutate({
                accessToken: authState.accessToken.accessToken,
            });
            void oktaAuth.handleLoginRedirect(
                {
                    ...authState,
                },
                typeof redirect === 'string' ? redirect : ''
            );
        }
    };

    useEffect(() => {
        if (authState?.isAuthenticated && authState.accessToken) {
            void handleAlreadyAuthenticated();
            return;
        }
    }, [authState]);

    // this query param is set when the user authenticates via azure SSO
    if (typeof fromLogin === 'string' && !!fromLogin) {
        void oktaAuth.signInWithRedirect({
            originalUri: typeof redirect === 'string' ? redirect : '',
            redirectUri: window.location.origin + '/cb',
        });
        return;
    }

    return (
        <div className={`${styles['login-container']} login-container`}>
            <UiKit.Chakra.Box height={'100vh'}>
                <UiKit.Chakra.VStack justify="center" pt={isSmallScreen ? '20px' : '36px'}>
                    <UiKit.Chakra.Spacer />
                    <Image
                        src="/ciox-is-now-datavant.svg"
                        alt="Datavant (formerly Ciox Health) Logo"
                        priority
                        height={isSmallScreen ? 48 : 80}
                        width={isSmallScreen ? 173 : 288}
                    />
                    <UiKit.Chakra.Spacer />
                </UiKit.Chakra.VStack>

                <UiKit.Chakra.Box
                    backgroundColor={'#ffffff'}
                    width={'fit-content'}
                    height={'fit-content'}
                    marginTop={'8px'}
                    padding={'24px 48px'}
                    borderRadius={'12px'}
                    minHeight={300}
                >
                    <UiKit.Chakra.VStack justify="center" spacing={'32px'}>
                        {autoForwarded && (
                            <ColoredBox
                                color={UiKit.consistency.brandColors.infoAlert.background}
                                borderColor={UiKit.consistency.brandColors.infoAlert.background}
                                title={'Your security and efficiency have been upgraded!'}
                                icon={<UiKit.Icons.Status status="info" />}
                            >
                                <UiKit.Text.Normal>
                                    Taking five minutes to create an account will unlock these streamlined
                                    features to help you manage your requests efficiently.
                                </UiKit.Text.Normal>
                            </ColoredBox>
                        )}
                        <UiKit.Chakra.HStack
                            divider={<UiKit.Chakra.StackDivider borderColor="gray.200" />}
                            width={'fit-content'}
                            spacing={'48px'}
                            align={'flex-start'}
                        >
                            <UiKit.Chakra.VStack spacing={'8px'} align={'flex-start'}>
                                <UiKit.Text.Heading1 text="Request Manager" />
                                <div className={styles['rm-info']}>
                                    Upgraded Provider Portal - Request Manager is a single login for all of
                                    your Ciox/Datavant risk adjustment requests from payers
                                </div>
                                <UiKit.Chakra.HStack>
                                    <UiKit.Icons.FontAwesomeIcon
                                        icon={UiKit.Icons.FontAwesome.solid.faPhoneSlash}
                                        color={ICON.color}
                                        size={ICON.size}
                                    />
                                    <div className={styles['rm-info']}>Minimize faxes and phone calls</div>
                                </UiKit.Chakra.HStack>
                                <UiKit.Chakra.HStack>
                                    <UiKit.Icons.FontAwesomeIcon
                                        icon={UiKit.Icons.FontAwesome.solid.faListOl}
                                        color={ICON.color}
                                        size={ICON.size}
                                    />
                                    <div className={styles['rm-info']}>
                                        See all open requests in one queue
                                    </div>
                                </UiKit.Chakra.HStack>
                                <UiKit.Chakra.HStack>
                                    <UiKit.Icons.FontAwesomeIcon
                                        icon={UiKit.Icons.FontAwesome.solid.faCloudArrowUp}
                                        color={ICON.color}
                                        size={ICON.size}
                                    />
                                    <div className={styles['rm-info']}>Digitally upload records</div>
                                </UiKit.Chakra.HStack>
                                <UiKit.Chakra.HStack>
                                    <UiKit.Icons.FontAwesomeIcon
                                        icon={UiKit.Icons.FontAwesome.solid.faHome}
                                        color={ICON.color}
                                        size={ICON.size}
                                    />
                                    <div className={styles['rm-info']}>Keep addresses up to date</div>
                                </UiKit.Chakra.HStack>
                                <UiKit.Chakra.HStack>
                                    <UiKit.Icons.FontAwesomeIcon
                                        icon={UiKit.Icons.FontAwesome.solid.faInfoCircle}
                                        color={ICON.color}
                                        size={ICON.size}
                                    />
                                    <div className={styles['rm-info']}>
                                        Let us know when you can't fulfill a record and why
                                    </div>
                                </UiKit.Chakra.HStack>
                                <UiKit.Chakra.VStack
                                    pt={'30px'}
                                    spacing={'16px'}
                                    align={'flex-start'}
                                    width={'fit-content'}
                                >
                                    <UiKit.Text.BodyLarge>Before you Start:</UiKit.Text.BodyLarge>
                                    <UiKit.Chakra.HStack alignItems={'stretch'} gap={1}>
                                        <UiKit.Text.BodyRegular>Find a recent</UiKit.Text.BodyRegular>
                                        <UiKit.Text.BodyRegularBold>
                                            Datavant Packet
                                        </UiKit.Text.BodyRegularBold>
                                        <UiKit.Text.BodyRegular>
                                            (fax request cover letter) to reference.
                                        </UiKit.Text.BodyRegular>
                                    </UiKit.Chakra.HStack>
                                </UiKit.Chakra.VStack>
                            </UiKit.Chakra.VStack>
                            <UiKit.Chakra.Box width={400}>
                                {flow === null && (
                                    <EmailDiscovery
                                        onEmailDiscovered={({ doesUserExist, email }) => {
                                            setEmail(email);
                                            setFlow(doesUserExist ? 'login' : 'register');
                                        }}
                                    />
                                )}
                                {flow === 'login' && (
                                    <EmbeddedLogin
                                        username={email}
                                        onBackToSignInClick={() => setFlow(null)}
                                    />
                                )}
                                {flow === 'register' && (
                                    <Register
                                        email={email}
                                        onBackToSignInClick={() => setFlow(null)}
                                        onRegisterSuccess={() => {
                                            datadogLogs.logger.notice(
                                                'User registered using embedded auth flow'
                                            );
                                            toast({
                                                title: `You have successfully registered. Returning to login.`,
                                                status: 'success',
                                                duration: 5000,
                                                isClosable: true,
                                                position: 'top-right',
                                                onCloseComplete: () => setFlow('login'),
                                            });
                                        }}
                                    />
                                )}
                            </UiKit.Chakra.Box>
                        </UiKit.Chakra.HStack>
                    </UiKit.Chakra.VStack>
                </UiKit.Chakra.Box>
                <UiKit.Chakra.Flex width="100%" align="top" mt={5}>
                    <UiKit.Chakra.Box flex="1" />
                    <UiKit.Chakra.Box>
                        <HelpFooter />
                    </UiKit.Chakra.Box>
                    <UiKit.Chakra.Box flex="1" display="flex" justifyContent="flex-end">
                        {autoForwarded && (
                            <UiKit.Button.Cancel
                                text="Finish Account Set Up Later"
                                id="finish-account-setup-later"
                                color={ICON.color}
                                onClick={async () => {
                                    await tRPCProxyClient.requestManager.rmBasic.optOutRMStandard.query();
                                    window.location.href = `${window.location.origin}/provider/upload`;
                                }}
                            />
                        )}
                    </UiKit.Chakra.Box>
                </UiKit.Chakra.Flex>
            </UiKit.Chakra.Box>
        </div>
    );
};
